// Pages/admin/SubscriptionsPage.jsx (version modernisée)
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiEye,
  FiToggleLeft,
  FiToggleRight,
  FiPackage,
  FiDollarSign,
  FiActivity,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiImage,
} from "react-icons/fi";
import { GiPayMoney } from "react-icons/gi";
import { BiCopy } from "react-icons/bi";

import { useSubscriptionPlanStore } from "../store/subscriptionPlanStore";
import { toast } from "react-toastify";
import Breadcrumb from "./../components/Breadcrumb";
import SubscribeModal from "./../components/SubscribeModal";
import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";

// Composant ServiceImage optimisé
function ServiceImage({ service, isActive = false }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const getImageUrl = () => {
    if (!service?.imageUrl) return null;

    if (service.imageUrl.startsWith("http")) {
      return service.imageUrl;
    }

    return `${process.env.REACT_APP_ENV === "development"
      ? process.env.REACT_APP_DEV_SERVICE_IMG
      : process.env.REACT_APP_API_SERVICE_IMG
      }/${service.imageUrl}`;
  };

  const imageUrl = getImageUrl();

  return (
    <div
      className={`w-full h-full transition-all duration-500 absolute top-0 left-0 ${isActive ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
        }`}
    >
      {imageUrl && !error ? (
        <>
          <img
            src={imageUrl}
            alt={`${service?.nom || "service"}`}
            loading="lazy"
            className={`w-full h-full object-cover transition-all duration-700 ${loaded ? "opacity-100" : "opacity-0"
              }`}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
          {/* Overlay dégradé pour le texte */}
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent" />

          {/* Nom du service */}
          <div className="absolute bottom-4 left-4 right-4">
            <h4 className="text-white font-poppins font-semibold text-sm truncate">
              {service?.nom || "Service"}
            </h4>
            <p className="text-white/80 font-nunito text-xs truncate">
              {service?.description?.substring(0, 60) ||
                "Description non disponible"}
              ...
            </p>
          </div>
        </>
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center p-4">
          <div className="p-3 rounded-none bg-secondary-100 text-secondary-600 mb-3">
            <FiImage className="h-8 w-8" />
          </div>
          <span className="text-gray-600 text-sm font-medium text-center">
            {service?.nom || "Service"}
          </span>
          <span className="text-gray-400 text-xs mt-1">
            {error ? "Image non chargée" : "Pas d'image"}
          </span>
        </div>
      )}
    </div>
  );
}

// Composant ImageCarousel pour le slide - version corrigée
function ImageCarousel({ services }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Filtrer les services valides
  const validServices = services?.filter((item) => item?.service) || [];

  // Auto-slide toutes les 5 secondes
  useEffect(() => {
    if (validServices.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === validServices.length - 1 ? 0 : prevIndex + 1,
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, validServices.length]);

  if (validServices.length === 0) {
    return (
      <div className="relative h-48 bg-gradient-to-br from-secondary-50 to-gray-100 rounded-none overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <FiPackage className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
          <span className="text-secondary-600 font-medium text-sm">
            Aucune image disponible
          </span>
        </div>
      </div>
    );
  }

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === validServices.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? validServices.length - 1 : prevIndex - 1,
    );
  };

  return (
    <div className="relative h-48 rounded-none overflow-hidden group">
      {/* Conteneur des images */}
      <div className="relative w-full h-full">
        {validServices.map((includedService, index) => (
          <ServiceImage
            key={`${includedService.id}-${index}`}
            service={includedService.service}
            isActive={index === currentIndex}
          />
        ))}

        {/* Fond de secours si aucune image ne charge */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 -z-10" />
      </div>

      {/* Flèches de navigation */}
      {validServices.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-none opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-20"
            aria-label="Image précédente"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-none opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-20"
            aria-label="Image suivante"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </>
      )}

      {/* Indicateurs de slide (dots) */}
      {validServices.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-2 z-20">
          {validServices.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                goToSlide(index);
              }}
              className={`w-2 h-2 rounded-none transition-all duration-300 ${index === currentIndex
                ? "bg-white w-6"
                : "bg-white/50 hover:bg-white/80"
                }`}
              aria-label={`Aller à l'image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Badge du nombre de services */}
      <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-none shadow-sm z-20">
        <span className="text-xs font-bold text-gray-800 flex items-center gap-1">
          <FiPackage className="w-3 h-3" />
          {validServices.length} service{validServices.length > 1 ? "s" : ""}
        </span>
      </div>

      {/* Compteur d'images */}
      {validServices.length > 1 && (
        <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-none text-xs font-medium z-20">
          {currentIndex + 1} / {validServices.length}
        </div>
      )}
    </div>
  );
}

export default function PublicSubscriptionsPage() {
  const { subscriptions, loading, error, fetchSubscriptionsActif } =
    useSubscriptionPlanStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [subscribeSelected, setSubscribeSelected] = useState(null);

  useEffect(() => {
    fetchSubscriptionsActif();
  }, [fetchSubscriptionsActif]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XAF",
    }).format(price);
  };

  // Filtrage des abonnements
  const filteredSubscriptions = subscriptions.filter((subscription) => {
    const term = searchTerm.toLowerCase();

    const matchesSearch =
      subscription.name.toLowerCase().includes(term) ||
      subscription.description.toLowerCase().includes(term) ||
      subscription.code.toLowerCase().includes(term) ||
      // Recherche dans chaque service inclus
      subscription.includedServices.some((item) =>
        item?.service?.nom.toLowerCase().includes(term),
      );

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && subscription.isActive) ||
      (statusFilter === "inactive" && !subscription.isActive);

    return matchesSearch && matchesStatus;
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  const handleSubscription = (subscription) => {
    setSubscribeSelected(subscription);
    setShowSubscribeModal(true);
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen px-4 py-10 max-w-7xl mx-auto">
      {/* Titre */}
      <div className="mb-8">
        <h2 className="font-poppins font-bold text-2xl text-gray-800 mb-2 flex items-center">
          <FiPackage className="mr-3 text-secondary-600" size={36} />
          Plans d'abonnements
        </h2>
        <p className="font-nunito text-gray-600 text-sm">
          Découvrez nos formules et choisissez celle qui vous correspond
        </p>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key="step2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          {/* Barre de recherche et filtres */}
          <div className="bg-white p-6 rounded-none shadow-sm border border-gray-100 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="relative w-full md:w-auto md:flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" size={20} />
                </div>
                <input
                  type="text"
                  placeholder="Rechercher un abonnement..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-none focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200 font-nunito text-sm bg-gray-50"
                />
              </div>

              <div className="flex items-center gap-3 w-full md:w-auto">
                {/* <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200 font-nunito text-sm bg-gray-50"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="active">Actifs seulement</option>
                  <option value="inactive">Inactifs</option>
                </select> */}

                <button
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    fetchSubscriptionsActif();
                  }}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-none transition-all duration-200 font-nunito font-semibold text-sm flex items-center gap-2"
                >
                  <FiRefreshCw size={16} />
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="mb-6 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-none p-6">
              <div className="flex items-center space-x-3 text-red-800">
                <FiActivity size={24} />
                <div>
                  <span className="font-poppins font-medium text-sm">
                    {error}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Grille des abonnements - 3 colonnes sur grands écrans */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredSubscriptions.map((subscription) => (
              <motion.div
                key={subscription.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group flex flex-col h-full bg-white rounded-none shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-secondary-200"
              >
                {/* SECTION 1 : Images en stack */}
                <div className="relative">
                  <ImageCarousel services={subscription.includedServices} />

                  {/* <div className="absolute top-3 left-12 z-25">
                    <span
                      className={`px-3 py-1 rounded-none text-xs font-semibold backdrop-blur-sm ${
                        subscription.isActive
                          ? "bg-green-500/90 text-white"
                          : "bg-gray-500/90 text-white"
                      }`}
                    >
                      {subscription.isActive ? "✓ Actif" : "Inactif"}
                    </span>
                  </div>

                  <div className="absolute top-48 right-4 z-20">
                    <div className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-4 py-2 rounded-none shadow-lg">
                      <span className="font-bold text-lg">
                        {formatPrice(subscription.price)}
                      </span>
                    </div>
                  </div> */}
                </div>

                {/* SECTION 2 : Contenu avec scroll */}
                <div className="flex flex-col flex-1 p-6 overflow-hidden">
                  {/* En-tête */}
                  <div className="mb-4">
                    <h3 className="font-poppins font-bold text-center text-xl text-gray-800 mb-2 line-clamp-1">
                      {subscription.name}
                    </h3>

                    <div className="flex items-center justify-center bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-4 py-2 rounded-none shadow-lg">
                      <span className="font-bold text-lg">
                        {formatPrice(subscription.price)}
                      </span>
                    </div>

                    {/* <div className="flex items-center gap-2 mb-3">
                      <span className="font-nunito text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-none">
                        {subscription.code}
                      </span>
                      <button
                        onClick={() => copyToClipboard(subscription.code)}
                        className="p-1 text-gray-400 hover:text-secondary-600 transition-colors"
                        title="Copier le code"
                      >
                        <BiCopy size={14} />
                      </button>
                    </div> */}

                    <p className="mt-3 font-poppins text-sm text-gray-600 mb-4 line-clamp-2">
                      {subscription.description}
                    </p>
                  </div>

                  {/* Liste des services avec scroll */}
                  <div className="flex-1 overflow-hidden mb-4">
                    <h4 className="font-poppins font-semibold text-sm text-gray-700 mb-3 flex items-center">
                      <FiPackage
                        className="mr-2 text-secondary-500"
                        size={14}
                      />
                      Services inclus
                    </h4>

                    <div className="h-40 overflow-y-auto pr-2 custom-scrollbar">
                      {subscription.includedServices &&
                        subscription.includedServices.length > 0 ? (
                        <ul className="space-y-3">
                          {subscription.includedServices.map(
                            (includedService, index) => (
                              <motion.li
                                key={includedService.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-none hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex-1 min-w-0">
                                  <span className="font-nunito font-medium text-sm text-gray-800 truncate block">
                                    {includedService.service?.nom ||
                                      `Service #${includedService.id}`}
                                  </span>
                                  <span className="font-nunito text-xs text-gray-500">
                                    Durée:{" "}
                                    {includedService.service?.durationMinutes ||
                                      "N/A"}{" "}
                                    min
                                  </span>
                                </div>
                                <span className="font-poppins font-bold text-secondary-600 bg-secondary-50 px-3 py-1 rounded-none text-sm min-w-[3rem] text-center">
                                  ×{includedService.quantity}
                                </span>
                              </motion.li>
                            ),
                          )}
                        </ul>
                      ) : (
                        <div className="text-center py-8">
                          <FiPackage
                            className="mx-auto text-gray-300 mb-3"
                            size={32}
                          />
                          <p className="font-nunito text-sm text-gray-500 italic">
                            Aucun service inclus
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SECTION 3 : Bouton de souscription */}
                <div className="p-6 pt-0">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleSubscription(subscription)}
                    className="w-full bg-gradient-to-r from-secondary-500 via-secondary-600 to-secondary-700 hover:from-secondary-600 hover:via-secondary-700 hover:to-secondary-800 text-white px-6 py-4 rounded-none font-poppins font-bold text-sm uppercase tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 group/btn"
                    disabled={!subscription.isActive}
                  >
                    <GiPayMoney
                      className="group-hover/btn:scale-110 transition-transform duration-300"
                      size={18}
                    />
                    <span className="group-hover/btn:translate-x-1 transition-transform duration-300">
                      {subscription.isActive
                        ? "Souscrire maintenant"
                        : "Indisponible"}
                    </span>
                    <motion.span
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"
                    >
                      →
                    </motion.span>
                  </motion.button>

                  {!subscription.isActive && (
                    <p className="text-center text-gray-500 text-xs mt-2 font-nunito">
                      Ce plan n'est plus disponible
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Message si aucun abonnement */}
          {filteredSubscriptions.length === 0 && !loading && (
            <div className="text-center py-20 bg-gradient-to-b from-white to-gray-50 rounded-none shadow-sm">
              <div className="inline-block p-6 bg-gradient-to-r from-gray-100 to-gray-200 rounded-none mb-6">
                <FiPackage className="mx-auto text-gray-400" size={64} />
              </div>
              <h3 className="font-poppins font-bold text-xl text-gray-800 mb-3">
                Aucun abonnement trouvé
              </h3>
              <p className="font-nunito text-gray-600 text-sm mb-8 max-w-md mx-auto">
                {searchTerm || statusFilter !== "all"
                  ? "Aucun abonnement ne correspond à vos critères de recherche. Essayez de modifier vos filtres."
                  : "Aucun plan d'abonnement n'est disponible pour le moment."}
              </p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Modal de souscription */}
      <SubscribeModal
        isOpen={showSubscribeModal}
        onClose={() => {
          setShowSubscribeModal(false);
        }}
        plan={subscribeSelected}
      />

      {/* Styles pour la scrollbar personnalisée */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 0;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 0;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }
      `}</style>
    </div>
  );
}

// // Pages/admin/SubscriptionsPage.jsx (version modernisée)
// import { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import {
//   FiPlus,
//   FiEdit3,
//   FiTrash2,
//   FiEye,
//   FiToggleLeft,
//   FiToggleRight,
//   FiPackage,
//   FiDollarSign,
//   FiActivity,
//   FiSearch,
//   FiFilter,
//   FiRefreshCw,
// } from "react-icons/fi";
// import { GiPayMoney } from "react-icons/gi";
// import { BiCopy } from "react-icons/bi";

// import { useSubscriptionPlanStore } from "../store/subscriptionPlanStore";
// import { toast } from "react-toastify";
// import Breadcrumb from "./../components/Breadcrumb";
// import SubscribeModal from "./../components/SubscribeModal";
// import { AnimatePresence } from "framer-motion";
// import { motion } from "framer-motion";

// export default function PublicSubscriptionsPage() {
//   const { subscriptions, loading, error, fetchSubscriptionsActif } =
//     useSubscriptionPlanStore();

//   const [searchTerm, setSearchTerm] = useState("");
//   const [statusFilter, setStatusFilter] = useState("active");

//   const [showSubscribeModal, setShowSubscribeModal] = useState(false);
//   const [subscribeSelected, setSubscribeSelected] = useState(null);

//   useEffect(() => {
//     fetchSubscriptionsActif();
//   }, [fetchSubscriptionsActif]);

//   const formatPrice = (price) => {
//     return new Intl.NumberFormat("fr-FR", {
//       style: "currency",
//       currency: "XAF",
//     }).format(price);
//   };

//   // Filtrage des abonnements
//   const filteredSubscriptions = subscriptions.filter((subscription) => {
//     const term = searchTerm.toLowerCase();

//     const matchesSearch =
//       subscription.name.toLowerCase().includes(term) ||
//       subscription.description.toLowerCase().includes(term) ||
//       subscription.code.toLowerCase().includes(term) ||
//       // Recherche dans chaque service inclus
//       subscription.includedServices.some((item) =>
//         item?.service?.nom.toLowerCase().includes(term),
//       );

//     const matchesStatus =
//       statusFilter === "all" ||
//       (statusFilter === "active" && subscription.isActive) ||
//       (statusFilter === "inactive" && !subscription.isActive);

//     return matchesSearch && matchesStatus;
//   });

//   const copyToClipboard = (text) => {
//     navigator.clipboard.writeText(text);
//     toast.success("Copié dans le presse-papier");
//   };

//   const handleSubscription = (subscription) => {
//     setSubscribeSelected(subscription);
//     setShowSubscribeModal(true);
//   };

//   return (
//     <div className="bg-gray-50 min-h-screen px-4 py-10 max-w-7xl mx-auto">
//       {/* Titre */}
//       <div className="mb-8">
//         <h2 className="font-poppins font-semibold text-[18px] text-title">
//           <FiPackage className="inline mr-4 text-secondary-600" size={36} />
//           Plan d'abonnements
//         </h2>
//         {/* <Breadcrumb
//           items={[
//             { label: "Accueil", link: "/" },
//             { label: "Plan d'abonnements", link: "/plan-abonnements" },
//             // { label: `${servicesSelected?.nom || ""}`, link: "/services" },
//           ]}
//         /> */}
//       </div>

//       <AnimatePresence mode="wait">
//         <motion.div
//           key="step2"
//           initial={{ opacity: 0, x: -50 }}
//           animate={{ opacity: 1, x: 0 }}
//           exit={{ opacity: 0, x: 50 }}
//           transition={{ duration: 0.4 }}
//         >
//           {/* Barre de recherche et filtres */}
//           <div className="bg-white p-1 md:p-6 shadow-sm border border-gray-200 mb-6">
//             <div className="md:flex items-center space-x-4 space-y-4 md:space-y-0">
//               <div className="relative">
//                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                   <FiSearch className="text-gray-400" size={20} />
//                 </div>
//                 <input
//                   type="text"
//                   placeholder="Rechercher un abonnement..."
//                   value={searchTerm}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                   className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-all duration-200 font-nunito text-[14px]"
//                 />
//               </div>
//               <button
//                 onClick={() => {
//                   setSearchTerm("");
//                   setStatusFilter("all");
//                   fetchSubscriptionsActif();
//                 }}
//                 className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-3 transition-all duration-200 font-nunito font-semibold text-[14px]"
//               >
//                 <FiRefreshCw size={18} />
//               </button>
//             </div>
//           </div>

//           {/* Message d'erreur */}
//           {error && (
//             <div className="mb-6 bg-red-50 border border-red-200  p-6">
//               <div className="flex items-center space-x-3 text-red-800">
//                 <FiActivity size={24} />
//                 <div>
//                   <span className="font-poppins font-medium text-[15px]">
//                     {error}
//                   </span>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Grille des abonnements */}
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             {filteredSubscriptions.map((subscription) => (
//               <div
//                 key={subscription.id}
//                 className="bg-white border border-gray-200  shadow-sm hover:shadow-xl transition-all duration-300 group"
//               >
//                 {/* En-tête de la carte */}
//                 <div
//                   className={`p-2 md:p-6 border-b ${
//                     subscription.isActive
//                       ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
//                       : "bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200"
//                   }`}
//                 >
//                   <div className="flex items-center space-x-3">
//                     <h3 className="font-poppins font-semibold text-[18px] text-title">
//                       {subscription.name}
//                     </h3>
//                   </div>

//                   <div className="flex items-center gap-2 font-nunito text-[14px] mb-2">
//                     <label className="font-poppins font-medium text-[15px] text-text">
//                       Code :
//                     </label>

//                     <div
//                       className="flex items-center gap-2 cursor-pointer"
//                       onClick={() => copyToClipboard(subscription.code)}
//                     >
//                       {/* <span className="font-mono bg-gray-100 px-3 py-2 flex-1 text-[14px]"> */}
//                       <span className="bg-gray-100 px-3 py-2 flex-1 font-nunito text-[14px]">
//                         {subscription.code}
//                       </span>
//                       <button
//                         onClick={() => copyToClipboard(subscription.code)}
//                         className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
//                       >
//                         <BiCopy size={16} />
//                       </button>
//                     </div>
//                   </div>

//                   <p className="font-nunito text-[14px] text-text mb-2 line-clamp-2">
//                     {subscription.description}
//                   </p>

//                   <div className="flex items-center justify-between">
//                     <span className="font-poppins font-bold text-[16px] text-montant">
//                       {formatPrice(subscription.price)}
//                     </span>
//                     <span
//                       className={`px-3 py-1 font-nunito font-semibold text-[14px] ${
//                         subscription.isActive
//                           ? "bg-green-100 text-green-800 border border-green-200"
//                           : "bg-gray-100 text-gray-800 border border-gray-200"
//                       }`}
//                     >
//                       {subscription.isActive ? "Actif" : "Inactif"}
//                     </span>
//                   </div>
//                 </div>

//                 {/* Corps de la carte */}
//                 <div className="p-2 md:p-6">
//                   <div className="mb-4">
//                     <h4 className="font-poppins font-medium text-[15px] text-title mb-3 flex items-center">
//                       <FiPackage
//                         className="mr-2 text-secondary-500"
//                         size={16}
//                       />
//                       Services inclus
//                     </h4>
//                     {subscription.includedServices &&
//                     subscription.includedServices.length > 0 ? (
//                       <ul className="space-y-2">
//                         {subscription.includedServices.map(
//                           (includedService, index) => (
//                             <li
//                               key={index}
//                               className="flex items-center justify-between font-nunito text-[14px]"
//                             >
//                               <span className="text-text">
//                                 {includedService.service?.nom ||
//                                   `Service #${includedService.id}`}
//                               </span>
//                               <span className="text-gray-400 font-poppins font-medium text-[15px]">
//                                 ×{includedService.quantity}
//                               </span>
//                             </li>
//                           ),
//                         )}
//                       </ul>
//                     ) : (
//                       <p className="font-nunito text-[14px] text-gray-500 italic">
//                         Aucun service inclus
//                       </p>
//                     )}
//                   </div>

//                   {/* Actions */}
//                   <div className="flex justify-end items-center space-x-2">
//                     <button
//                       onClick={() => {
//                         handleSubscription(subscription);
//                       }}
//                       className="flex-1 bg-secondary-50 hover:bg-secondary-100 text-secondary-600 px-4 py-3 font-nunito font-semibold text-[14px] text-center transition-all duration-200 hover:shadow-sm inline-flex items-center justify-center space-x-2"
//                       title="Souscrire"
//                     >
//                       <GiPayMoney size={16} />
//                       <span className="">Souscrire</span>
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>

//           {/* Message si aucun abonnement */}
//           {filteredSubscriptions.length === 0 && !loading && (
//             <div className="text-center py-16">
//               <FiPackage className="mx-auto text-gray-400 mb-4" size={64} />
//               <h3 className="font-poppins font-semibold text-[18px] text-title mb-3">
//                 Aucun abonnement trouvé
//               </h3>
//               <p className="font-nunito text-[14px] text-text mb-8">
//                 {searchTerm || statusFilter !== "all"
//                   ? "Aucun abonnement ne correspond à vos critères de recherche"
//                   : ""}
//               </p>
//             </div>
//           )}
//         </motion.div>
//       </AnimatePresence>

//       {/* Modal de souscription */}
//       <SubscribeModal
//         isOpen={showSubscribeModal}
//         onClose={() => {
//           setShowSubscribeModal(false);
//         }}
//         plan={subscribeSelected}
//         // onSuccess={selectedDateTime}
//       />
//     </div>
//   );
// }
